import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Navigation, A11y } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { SwiperOptions } from "swiper/types";
import { Spinner } from "../Spinner";
import { SearchIconSvg, CloseIconSvg, CaretIconSvg } from "../icons";
import { colors } from "../../styles/variables";
import { NoResults } from "./NoResults";
import {
  StyledSearchOverlay,
  StyledSearchContainer,
  StyledSearchContent,
  StyledSearchInputContainer,
  StyledInputWrapper,
  StyledSearchIconWrapper,
  StyledInput,
  StyledClearButton,
  StyledArrowButton,
  StyledMainSection,
  StyledProductCard,
  StyledProductImage,
  StyledProductName,
  StyledProductPrice,
  StyledCloseContainer,
  StyledCloseButton,
  StyledSpinnerWrapper,
  StyledErrorMessage,
  StyledSwiperWrapper,
} from "./index.styled";

// Constants
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;

// Expected variant structure - apps should use their VariantFragment type
export interface SearchVariant {
  gtin: string;
  title?: string | null;
  price?: number | null;
  imgUrls?: (string | null | undefined)[] | null;
  slug?: string | null;
}

export interface AlgoliaSearchProps<
  TVariant extends SearchVariant = SearchVariant
> {
  /** Callback when close button is clicked */
  onCloseSearchClicked: () => void;

  /**
   * Callback when user performs a search
   * Parent component should execute the search query with this term
   */
  onSearch: (searchTerm: string) => void;

  /**
   * Search results to display
   * Should be VariantFragment[] from your generated types
   * Defaults to empty array
   */
  results?: TVariant[];

  /**
   * Loading state from parent's query
   * Defaults to false
   */
  loading?: boolean;

  /** Error state from parent's query */
  error?: string | null;

  /**
   * Optional function to format price display
   * Default: displays price in cents as dollars with $ prefix
   */
  formatPrice?: (price: number) => string;
}

export const AlgoliaSearch = <TVariant extends SearchVariant = SearchVariant>({
  onCloseSearchClicked,
  onSearch,
  results = [],
  loading = false,
  error,
  formatPrice = (price) => `$${(price / 100).toFixed(2)}`,
}: AlgoliaSearchProps<TVariant>) => {
  const [searchQuery, setSearchQuery] = useState("");
  const lastSearchedQuery = useRef<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Memoize handlers to prevent unnecessary re-renders
  const handleClear = useCallback(() => {
    setSearchQuery("");
    lastSearchedQuery.current = "";
    inputRef.current?.focus();
  }, []);

  const handleSearch = useCallback(() => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery && trimmedQuery !== lastSearchedQuery.current) {
      lastSearchedQuery.current = trimmedQuery;
      onSearch(trimmedQuery);
    }
  }, [searchQuery, onSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  // Auto-search with debouncing as user types
  useEffect(() => {
    const trimmedQuery = searchQuery.trim();

    // Only search if there are at least minimum characters
    if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
      return;
    }

    // Don't search if this is the same query we just searched
    if (trimmedQuery === lastSearchedQuery.current) {
      return;
    }

    // Debounce the search
    const timeoutId = setTimeout(() => {
      lastSearchedQuery.current = trimmedQuery;
      onSearch(trimmedQuery);
    }, DEBOUNCE_MS);

    // Cleanup timeout on unmount or when searchQuery changes
    return () => clearTimeout(timeoutId);
  }, [searchQuery, onSearch]);

  // Compute whether to show results to avoid repeated complex conditionals
  const shouldShowResults = useMemo(
    () =>
      results.length > 0 &&
      !loading &&
      searchQuery &&
      searchQuery === lastSearchedQuery.current,
    [results.length, loading, searchQuery]
  );

  // Memoize swiper options to prevent Swiper re-initialization
  const swiperOptions: SwiperOptions = useMemo(
    () => ({
      slidesPerView: "auto",
      width: 100,
      spaceBetween: 20,
      navigation: true,
      grabCursor: true,
    }),
    []
  );

  return (
    <>
      <StyledSearchOverlay onClick={onCloseSearchClicked} />
      <StyledSearchContainer>
        <StyledSearchContent>
          <StyledSearchInputContainer>
            <StyledInputWrapper>
              <StyledSearchIconWrapper>
                <SearchIconSvg />
              </StyledSearchIconWrapper>
              <StyledInput
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search products..."
                aria-label="Search products"
                autoFocus
              />
              {searchQuery && (
                <StyledClearButton onClick={handleClear}>
                  Clear search
                </StyledClearButton>
              )}
              <StyledArrowButton onClick={handleSearch}>
                <CaretIconSvg
                  pointDirection="right"
                  width="16px"
                  height="16px"
                />
              </StyledArrowButton>
            </StyledInputWrapper>
          </StyledSearchInputContainer>

          <StyledMainSection aria-live="polite" aria-busy={loading}>
            {loading && (
              <StyledSpinnerWrapper>
                <Spinner dotSize="25px" />
              </StyledSpinnerWrapper>
            )}

            {error && <StyledErrorMessage>Error: {error}</StyledErrorMessage>}

            {!loading &&
              !error &&
              results.length === 0 &&
              lastSearchedQuery.current && (
                <NoResults term={lastSearchedQuery.current} />
              )}

            {shouldShowResults && (
              <StyledSwiperWrapper>
                <Swiper {...swiperOptions} modules={[Navigation, A11y]}>
                  {results.map((variant) => (
                    <SwiperSlide key={variant.gtin}>
                      <StyledProductCard
                        href={variant.slug ? `/product/${variant.slug}` : "#"}
                        onClick={(e) => {
                          if (!variant.slug) e.preventDefault();
                        }}
                      >
                        <StyledProductImage>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={variant.imgUrls?.[0] || ""}
                            alt={variant.title || "Product"}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </StyledProductImage>
                        <StyledProductName>
                          {variant.title || "Untitled Product"}
                        </StyledProductName>
                        <StyledProductPrice>
                          {formatPrice(variant.price || 0)}
                        </StyledProductPrice>
                      </StyledProductCard>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </StyledSwiperWrapper>
            )}
          </StyledMainSection>

          <StyledCloseContainer>
            <StyledCloseButton onClick={onCloseSearchClicked}>
              <CloseIconSvg height="16" width="16" color={colors.white} />
              Close search
            </StyledCloseButton>
          </StyledCloseContainer>
        </StyledSearchContent>
      </StyledSearchContainer>
    </>
  );
};
